import { useState } from 'react';

import { cn } from '../../../lib/utils';
import type { Spotlight, StatusFilter } from '../types';
import { formatDate } from '../utils';
import { SpotlightStatusBadge } from './StatusBadges';

interface SpotlightListProps {
  spotlights: Spotlight[];
  isLoading: boolean;
  selectedSpotlightId: string | null;
  isCreatingNew: boolean;
  onSelect: (id: string) => void;
}

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'archived', label: 'Archived' },
];

export function SpotlightList({
  spotlights,
  isLoading,
  selectedSpotlightId,
  isCreatingNew,
  onSelect,
}: SpotlightListProps) {
  const [filter, setFilter] = useState<StatusFilter>('all');

  const filtered =
    filter === 'all'
      ? spotlights
      : spotlights.filter((spotlight) => spotlight.status === filter);

  return (
    <div className="flex h-full flex-col rounded-xl border border-neutral-800/60">
      <div className="flex flex-wrap gap-1 border-b border-neutral-800/60 p-2">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              filter === key
                ? 'bg-neutral-800 text-white'
                : 'text-neutral-500 hover:text-neutral-300',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="size-5 animate-spin rounded-full border-2 border-neutral-700 border-t-neutral-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-neutral-500">
            {spotlights.length === 0
              ? 'No spotlights yet.'
              : 'No spotlights match this filter.'}
          </div>
        ) : (
          <ul className="divide-y divide-neutral-800/60">
            {filtered.map((spotlight) => {
              const isSelected =
                !isCreatingNew && spotlight.id === selectedSpotlightId;
              return (
                <li key={spotlight.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(spotlight.id)}
                    className={cn(
                      'block w-full px-4 py-3 text-left transition-colors hover:bg-neutral-800/30',
                      isSelected && 'bg-neutral-800/50',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-white">
                          {spotlight.themeTitle || 'Untitled'}
                        </div>
                        <div className="mt-0.5 text-xs text-neutral-500">
                          {spotlight.weekId} · {spotlight.appEntries.length} app
                          {spotlight.appEntries.length === 1 ? '' : 's'}
                        </div>
                      </div>
                      <SpotlightStatusBadge status={spotlight.status} />
                    </div>
                    <div className="mt-1.5 text-[11px] text-neutral-600">
                      {formatDate(spotlight.publishStartAt)} –{' '}
                      {formatDate(spotlight.publishEndAt)}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
